import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Upload,
  Image,
  Video,
  FileText,
  Trash2,
  Edit2,
  ExternalLink,
  X,
  Check,
  Loader2,
  Grid,
  List,
  Plus,
  Tag,
  ChevronLeft,
  ChevronRight,
  Copy,
  Pencil,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Sparkles,
  Star,
  Cloud,
  Link2,
  Radio,
  Inbox,
  Wand2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  mediaAPI,
  cloudStorageAPI,
  listeningAPI,
  getErrorMessage,
  type MediaSubmissionRecord,
  type UploadProgressCallback,
} from '../../services/api';
import { VideoTrimmer } from './VideoTrimmer';

/* ─── Types ─── */
interface MediaItem {
  id: string;
  url: string;
  thumbnail_url: string | null;
  filename: string;
  media_type: string;
  mime_type: string;
  width: number | null;
  height: number | null;
  duration: number | null;
  file_size: number;
  tags: string[];
  alt_text: string | null;
  ai_description: string | null;
  visual_description: string | null;
  caption_suggestions: string[];
  detected_objects: string[];
  smart_tags: string[];
  provenance_prompt: string | null;
  provenance_model: string | null;
  provenance_provider: string | null;
  source_metadata: Record<string, unknown> | null;
  enrichment_status: string | null;
  enrichment_error: string | null;
  enriched_at: string | null;
  internal_notes: string | null;
  collection_ids: string[];
  favorite: boolean;
  source_type: string | null;
  source_url: string | null;
  campaign_id: string | null;
  created_by: string | null;
  archived_at: string | null;
  transcode_status: string | null;
  versions: string[];
  created_at: string;
}

interface CloudAccountLite {
  id: string;
  provider: string;
  display_name: string;
  email?: string | null;
}

interface CloudFileLite {
  id: string;
  name: string;
  mime_type: string;
  type: string;
  thumbnail_url?: string | null;
  size?: number | null;
}

interface ListeningMentionLite {
  id: string;
  platform: string;
  author_name?: string | null;
  author_handle?: string | null;
  content?: string | null;
  media_url?: string | null;
  url?: string | null;
  discovered_at?: string | null;
}

interface RssDiscoveryItem {
  image_url: string;
  title?: string | null;
  item_url?: string | null;
  pub_date?: string | null;
  author?: string | null;
}

interface MediaUsage {
  post_count: number;
  page_count: number;
  posts: Array<{
    id: string;
    title: string;
    status: string;
    scheduled_at: string | null;
    platforms: string[];
  }>;
  pages: Array<{
    id: string;
    title: string;
    slug: string;
    is_published: boolean;
  }>;
}

type MediaUpdatePayload = {
  alt_text?: string;
  tags?: string[];
  filename?: string;
  collection_ids?: string[];
  favorite?: boolean;
  internal_notes?: string;
};

interface StorageQuota {
  plan: string;
  used_bytes: number;
  limit_bytes: number; // -1 = unlimited
  used_mb: number;
  limit_mb: number;    // -1 = unlimited
  percentage: number;
}

interface MediaStats {
  total: number;
  by_type: Record<string, number>;
  total_size: number;
  tags: string[];
  quota: StorageQuota;
}

type ViewMode = 'grid' | 'list';
type TypeFilter = 'all' | 'image' | 'video' | 'gif' | 'audio';

/* ─── Video Thumbnail cache & concurrency limiter (module-level) ─── */
const _thumbCache = new Map<string, string>(); // src → objectURL, survives remounts
const _thumbQueue: Array<() => void> = [];
let _thumbActive = 0;
const MAX_CONCURRENT_EXTRACTIONS = 2;

function _thumbNext() {
  while (_thumbActive < MAX_CONCURRENT_EXTRACTIONS && _thumbQueue.length > 0) {
    const job = _thumbQueue.shift()!;
    _thumbActive++;
    job();
  }
}

function destroyVideo(v: HTMLVideoElement) {
  v.onloadeddata = null;
  v.onseeked = null;
  v.onerror = null;
  v.pause();
  v.removeAttribute('src');
  v.load();          // release media buffers
}

/* ─── Video Thumbnail (lazy, cached, concurrency-limited) ─── */
const VideoThumbnail: React.FC<{ src: string; poster?: string | null; alt?: string; className?: string }> = ({ src, poster, alt, className = '' }) => {
  const cached = _thumbCache.get(src);
  const [thumb, setThumb] = useState<string | null>(poster || cached || null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const mountedRef = useRef(true);

  // Track mount state
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Reset when src changes
  useEffect(() => {
    const c = _thumbCache.get(src);
    setThumb(poster || c || null);
  }, [src, poster]);

  // Lazy: only start work when scrolled into view
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.disconnect(); } },
      { rootMargin: '100px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Extract thumbnail with concurrency limit
  useEffect(() => {
    if (!isVisible) return;
    // Already have a thumb (poster or cached)
    if (thumb) return;
    // Already in cache (race between mount and cache fill)
    if (_thumbCache.has(src)) {
      setThumb(_thumbCache.get(src)!);
      return;
    }

    let cancelled = false;

    const run = () => {
      if (cancelled) { _thumbActive--; _thumbNext(); return; }

      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.preload = 'metadata';

      const done = () => {
        destroyVideo(video);
        _thumbActive--;
        _thumbNext();
      };

      video.onloadeddata = () => {
        if (cancelled) { done(); return; }
        video.currentTime = Math.min(1, video.duration * 0.25);
      };

      video.onseeked = () => {
        if (cancelled) { done(); return; }
        try {
          const MAX = 160; // small thumbnails to save memory
          const scale = Math.min(1, MAX / Math.max(video.videoWidth || 1, video.videoHeight || 1));
          const w = Math.round((video.videoWidth || 160) * scale);
          const h = Math.round((video.videoHeight || 90) * scale);
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, w, h);
            canvas.toBlob((blob) => {
              if (blob && !cancelled) {
                const url = URL.createObjectURL(blob);
                _thumbCache.set(src, url);
                if (mountedRef.current) setThumb(url);
              }
              done();
            }, 'image/jpeg', 0.4);
            return; // done() called inside toBlob callback
          }
        } catch {
          // CORS or other error
        }
        done();
      };

      video.onerror = () => done();
      video.src = src;
    };

    // Enqueue
    _thumbQueue.push(run);
    _thumbNext();

    return () => {
      cancelled = true;
      // Remove from queue if not yet started
      const idx = _thumbQueue.indexOf(run);
      if (idx !== -1) _thumbQueue.splice(idx, 1);
    };
  }, [src, thumb, isVisible]);

  if (thumb) {
    return <img src={thumb} alt={alt || ''} className={className} loading="lazy" />;
  }

  return (
    <div ref={containerRef} className={`flex items-center justify-center bg-gray-900 ${className}`}>
      <Video className="w-8 h-8 text-n-text-md" />
    </div>
  );
};

/* ─── Video Player ─── */
const VideoPlayer: React.FC<{ src: string; poster?: string }> = ({ src, poster }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setShowControls(true);
  }, [src]);

  // Clean up video element & hide timer on unmount
  useEffect(() => {
    const videoEl = videoRef.current;
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (videoEl) {
        videoEl.pause();
        videoEl.removeAttribute('src');
        videoEl.load();
      }
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setIsPlaying(true); }
    else { v.pause(); setIsPlaying(false); }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
    setCurrentTime(v.currentTime);
  };

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (v) setDuration(v.duration);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setShowControls(true);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    const bar = progressRef.current;
    if (!v || !bar || !v.duration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = pct * v.duration;
  };

  const handleFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (isPlaying) {
      hideTimer.current = setTimeout(() => setShowControls(false), 2500);
    }
  };

  const fmtTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className="relative rounded-n-sm overflow-hidden bg-black group cursor-pointer"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => { if (isPlaying) setShowControls(false); }}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted={isMuted}
        className="w-full max-h-56 object-contain"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onClick={togglePlay}
        preload="metadata"
      />

      {/* Big center play button (when paused) */}
      {!isPlaying && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 transition-all hover:bg-black/40"
        >
          <div className="w-14 h-14 rounded-full bg-n-card/90 backdrop-blur-sm flex items-center justify-center shadow-lg transition-transform hover:scale-110">
            <Play className="w-6 h-6 text-gray-800 ml-0.5" fill="currentColor" />
          </div>
        </button>
      )}

      {/* Bottom controls bar */}
      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-8 pb-2 px-3 transition-opacity duration-200 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}>
        {/* Progress bar */}
        <div
          ref={progressRef}
          role="button"
          tabIndex={0}
          onClick={handleProgressClick}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleProgressClick(e as unknown as React.MouseEvent<HTMLDivElement>); } }}
          className="w-full h-1 bg-n-card/30 rounded-full mb-2 cursor-pointer group/bar"
        >
          <div
            className="h-full rounded-full relative transition-all"
            style={{ width: `${progress}%`, backgroundColor: '#f97316' }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-n-card shadow opacity-0 group-hover/bar:opacity-100 transition-opacity" />
          </div>
        </div>

        {/* Controls row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={togglePlay} className="p-0.5 text-white hover:text-orange-400 transition-colors">
              {isPlaying ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4" fill="currentColor" />}
            </button>
            <button onClick={toggleMute} className="p-0.5 text-white hover:text-orange-400 transition-colors">
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <span className="text-[10px] text-white/80 font-mono tabular-nums">
              {fmtTime(currentTime)} / {fmtTime(duration)}
            </span>
          </div>
          <button onClick={handleFullscreen} className="p-0.5 text-white hover:text-orange-400 transition-colors">
            <Maximize className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Helpers ─── */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

type PlatformCompatibilityBadge = {
  id: string;
  label: string;
  tone: 'good' | 'warn';
};

function getPlatformCompatibilityBadges(item: MediaItem): PlatformCompatibilityBadge[] {
  const badges: PlatformCompatibilityBadge[] = [];
  const aspectRatio =
    item.width && item.height && item.height > 0 ? item.width / item.height : null;
  const isVertical = aspectRatio !== null && aspectRatio <= 0.8;
  const isPortrait = aspectRatio !== null && aspectRatio <= 1.0;
  const isLandscape = aspectRatio !== null && aspectRatio >= 1.35;
  const isSquareish = aspectRatio !== null && aspectRatio >= 0.9 && aspectRatio <= 1.12;

  if (item.media_type === 'video') {
    if (isVertical && item.duration !== null && item.duration <= 60) {
      badges.push({ id: 'instagram_reels', label: 'IG Reel', tone: 'good' });
      badges.push({ id: 'tiktok', label: 'TikTok', tone: 'good' });
      badges.push({ id: 'shorts', label: 'Shorts', tone: 'good' });
    } else if (isLandscape) {
      badges.push({ id: 'youtube', label: 'YouTube', tone: 'good' });
      badges.push({ id: 'linkedin_video', label: 'LinkedIn', tone: 'good' });
    } else {
      badges.push({ id: 'facebook_video', label: 'Facebook', tone: 'good' });
      badges.push({ id: 'x_video', label: 'X', tone: 'warn' });
    }
  } else {
    if (isVertical) {
      badges.push({ id: 'instagram_story', label: 'Story', tone: 'good' });
      badges.push({ id: 'pinterest', label: 'Pinterest', tone: 'good' });
      badges.push({ id: 'mobile_portrait', label: 'Mobile', tone: 'good' });
    } else if (isSquareish || isPortrait) {
      badges.push({ id: 'instagram_post', label: 'Instagram', tone: 'good' });
      badges.push({ id: 'facebook_post', label: 'Facebook', tone: 'good' });
      badges.push({ id: 'linkedin_post', label: 'LinkedIn', tone: 'good' });
    } else if (isLandscape) {
      badges.push({ id: 'x_card', label: 'X card', tone: 'good' });
      badges.push({ id: 'linkedin_banner', label: 'LinkedIn', tone: 'good' });
      badges.push({ id: 'youtube_thumb', label: 'Thumb', tone: 'warn' });
    }
  }

  return badges.slice(0, 4);
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function sourceProviderLabel(item: MediaItem): string | null {
  const provider = stringValue(item.source_metadata?.provider);
  if (!provider) return null;
  switch (provider) {
    case 'google_drive':
      return 'Google Drive';
    case 'dropbox':
      return 'Dropbox';
    case 'local_upload':
    case 'direct_upload':
      return 'Upload';
    case 'ai_image_generation':
      return 'AI image generation';
    default:
      return provider.replace(/[_-]/g, ' ');
  }
}

/* ─── Detail Panel ─── */
const DetailPanel: React.FC<{
  item: MediaItem;
  onClose: () => void;
  onDelete: () => void;
  onUpdate: (data: MediaUpdatePayload) => void;
  onArchive: () => void;
  onCreatePostFromMedia: () => void;
  onOpenInStudio: () => void;
  isDeleting: boolean;
  isUpdating: boolean;
  isArchiving: boolean;
  onTrimVideo?: () => void;
}> = ({
  item,
  onClose,
  onDelete,
  onUpdate,
  onArchive,
  onCreatePostFromMedia,
  onOpenInStudio,
  isDeleting,
  isUpdating,
  isArchiving,
  onTrimVideo,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: usageData, isLoading: isUsageLoading } = useQuery({
    queryKey: ['media-usage', item.id],
    queryFn: async () => {
      const { data } = await mediaAPI.usage(item.id);
      return data as MediaUsage;
    },
    staleTime: 30_000,
  });
  const { data: versionsData } = useQuery({
    queryKey: ['media-versions', item.id],
    queryFn: async () => {
      const { data } = await mediaAPI.versions(item.id);
      return data as { versions?: Record<string, { url?: string; width?: number; height?: number; size?: number }> };
    },
    staleTime: 60_000,
  });
  const [editingAlt, setEditingAlt] = useState(false);
  const [altText, setAltText] = useState(item.alt_text || '');
  const [editingName, setEditingName] = useState(false);
  const [filename, setFilename] = useState(item.filename);
  const [newTag, setNewTag] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState(item.internal_notes || '');
  const [variantPlatform, setVariantPlatform] = useState('instagram');
  const [variantStrategy, setVariantStrategy] = useState<'pad' | 'crop' | 'trim'>('pad');

  const sourceLabel =
    item.source_type === 'ai'
      ? t('media_source_ai', 'AI generated')
      : item.source_type === 'url'
        ? t('media_source_url', 'Imported from URL')
        : item.source_type === 'upload'
          ? t('media_source_upload', 'Uploaded')
          : item.source_type === 'cloud'
            ? t('media_source_cloud', 'Imported from cloud')
            : item.source_type
              ? item.source_type
              : t('media_source_unknown', 'Unknown source');

  const versionEntries = Object.entries(versionsData?.versions || {});
  const captionSuggestions = item.caption_suggestions || [];
  const smartTags = item.smart_tags || [];
  const detectedObjects = item.detected_objects || [];
  const sourceProvider = sourceProviderLabel(item);

  const refreshDrawerData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['media-library'] });
    queryClient.invalidateQueries({ queryKey: ['media-stats'] });
    queryClient.invalidateQueries({ queryKey: ['media-versions', item.id] });
  }, [item.id, queryClient]);

  const enrichMutation = useMutation({
    mutationFn: (overwriteAltText: boolean) =>
      mediaAPI.enrich(item.id, { overwrite_alt_text: overwriteAltText }),
    onSuccess: () => {
      toast.success(t('media_enriched', 'Asset enriched'));
      refreshDrawerData();
    },
    onError: (err) => toast.error(getErrorMessage(err, t('media_enrich_failed', 'Failed to enrich asset'))),
  });

  const captionMutation = useMutation({
    mutationFn: () => mediaAPI.captionSuggestions(item.id),
    onSuccess: () => {
      toast.success(t('media_captions_ready', 'Caption suggestions refreshed'));
      refreshDrawerData();
    },
    onError: (err) =>
      toast.error(getErrorMessage(err, t('media_captions_failed', 'Failed to refresh captions'))),
  });

  const platformReadyMutation = useMutation({
    mutationFn: () =>
      mediaAPI.makePlatformReady(item.id, {
        target_platforms: [variantPlatform],
        content_type: 'post',
        strategy: variantStrategy,
      }),
    onSuccess: () => {
      toast.success(t('media_platform_ready_done', 'Platform-ready version updated'));
      refreshDrawerData();
    },
    onError: (err) =>
      toast.error(
        getErrorMessage(err, t('media_platform_ready_failed', 'Failed to create platform-ready version'))
      ),
  });

  useEffect(() => {
    setAltText(item.alt_text || '');
    setFilename(item.filename);
    setNotes(item.internal_notes || '');
    setEditingAlt(false);
    setEditingName(false);
    setEditingNotes(false);
  }, [item.id, item.alt_text, item.filename, item.internal_notes]);

  const copyUrl = () => {
    navigator.clipboard.writeText(item.url || '');
    toast.success(t('media_url_copied', 'URL copied!'));
  };

  const addTag = () => {
    if (!newTag.trim()) return;
    const updated = [...(item.tags || []), newTag.trim().toLowerCase()];
    onUpdate({ tags: updated });
    setNewTag('');
  };

  const removeTag = (tag: string) => {
    onUpdate({ tags: (item.tags || []).filter((t) => t !== tag) });
  };

  return (
    <div className="w-80 border-l border-n-border bg-n-card flex-shrink-0 overflow-y-auto">
      <div className="p-5 border-b border-n-border flex items-center justify-between sticky top-0 bg-n-card z-10">
        <h3 className="text-sm font-semibold text-n-text">{t('media_details', 'Details')}</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onUpdate({ favorite: !item.favorite })}
            disabled={isUpdating}
            className={`p-1 rounded transition-colors ${
              item.favorite ? 'text-amber-500 hover:bg-amber-50' : 'text-n-text-lt hover:bg-n-page hover:text-n-text'
            }`}
            aria-label={item.favorite ? t('media_unfavorite', 'Remove favorite') : t('media_favorite', 'Favorite')}
          >
            <Star className="w-4 h-4" fill={item.favorite ? 'currentColor' : 'none'} />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-n-page rounded transition-colors">
            <X className="w-4 h-4 text-n-text-md" />
          </button>
        </div>
      </div>
      <div className="p-4">

        {/* Preview */}
        <div className="mb-4">
          {item.media_type === 'image' ? (
            <div className="rounded-n-sm overflow-hidden bg-n-page">
              <img src={item.thumbnail_url || item.url} alt={item.filename} className="w-full object-contain max-h-48" />
            </div>
          ) : item.media_type === 'video' ? (
            <VideoPlayer src={item.url} poster={item.thumbnail_url || undefined} />
          ) : (
            <div className="rounded-n-sm overflow-hidden bg-n-page w-full h-32 flex items-center justify-center">
              <FileText className="w-12 h-12 text-n-text-lt" />
            </div>
          )}
        </div>

        {/* Filename */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-n-text-md uppercase tracking-wider">{t('media_filename', 'Filename')}</h4>
          {editingName ? (
            <div className="flex gap-1 mt-1">
              <input
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
              />
              <button onClick={() => { onUpdate({ filename }); setEditingName(false); }}
                className="p-1 text-green-600 hover:bg-green-50 rounded">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={() => { setFilename(item.filename); setEditingName(false); }}
                className="p-1 text-red-600 hover:bg-red-50 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1 mt-1">
              <p className="text-sm text-gray-800 truncate flex-1">{item.filename}</p>
              <button onClick={() => setEditingName(true)} className="p-1 hover:bg-n-page rounded text-n-text-lt">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
          <div>
            <span className="text-n-text-md">{t('media_type', 'Type')}</span>
            <p className="text-gray-800 font-medium capitalize">{item.media_type}</p>
          </div>
          <div>
            <span className="text-n-text-md">{t('media_size', 'Size')}</span>
            <p className="text-gray-800 font-medium">{formatBytes(item.file_size)}</p>
          </div>
          {item.width && item.height && (
            <div>
              <span className="text-n-text-md">{t('media_dimensions', 'Dimensions')}</span>
              <p className="text-gray-800 font-medium">{item.width} × {item.height}</p>
            </div>
          )}
          {item.duration && (
            <div>
              <span className="text-n-text-md">{t('media_duration', 'Duration')}</span>
              <p className="text-gray-800 font-medium">{formatDuration(item.duration)}</p>
            </div>
          )}
          <div>
            <span className="text-n-text-md">{t('media_uploaded', 'Uploaded')}</span>
            <p className="text-gray-800 font-medium">{timeAgo(item.created_at)}</p>
          </div>
          <div>
            <span className="text-n-text-md">{t('media_format', 'Format')}</span>
            <p className="text-gray-800 font-medium">{item.mime_type?.split('/')[1]?.toUpperCase() || '—'}</p>
          </div>
        </div>

        {/* Alt Text */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-n-text-md uppercase tracking-wider">{t('media_alt_text', 'Alt Text')}</h4>
          {editingAlt ? (
            <div className="mt-1">
              <textarea
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded resize-none"
                rows={3}
                placeholder={t('media_alt_text_placeholder', 'Describe this image for accessibility...')}
              />
              <div className="flex gap-1 mt-1">
                <button onClick={() => { onUpdate({ alt_text: altText }); setEditingAlt(false); }}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-white rounded bg-[#2383e2]">
                  {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  {t('media_save', 'Save')}
                </button>
                <button onClick={() => { setAltText(item.alt_text || ''); setEditingAlt(false); }}
                  className="px-2 py-1 text-xs text-n-text-md hover:bg-n-page rounded">{t('media_cancel', 'Cancel')}</button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-1 mt-1">
              <p className="text-sm text-n-text-md flex-1">
                {item.alt_text || <span className="text-n-text-lt italic">{t('media_no_alt_text', 'No alt text')}</span>}
              </p>
              <button onClick={() => setEditingAlt(true)} className="p-1 hover:bg-n-page rounded text-n-text-lt flex-shrink-0">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="mb-4">
          <h4 className="text-xs font-medium text-n-text-md uppercase tracking-wider">{t('media_tags', 'Tags')}</h4>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {(item.tags || []).map((tag, idx) => (
              <span key={`${tag}-${idx}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-n-page text-xs text-n-text">
                {tag}
                <button onClick={() => removeTag(tag)} className="hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-1 mt-2">
            <input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTag()}
              placeholder={t('media_add_tag_placeholder', 'Add tag...')}
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
            />
            <button onClick={addTag} className="px-2 py-1 text-xs text-white rounded bg-[#2383e2]">
              <Tag className="w-3 h-3" />
            </button>
          </div>
        </div>

        <div className="mb-4 rounded-n border border-n-border bg-n-hover p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-n-text-md">
                {t('media_ai_workspace', 'AI workspace')}
              </h4>
              <p className="mt-1 text-xs leading-5 text-n-text-md">
                {t(
                  'media_ai_workspace_hint',
                  'Refresh metadata, generate caption ideas, or create a platform-ready variant without leaving the library.'
                )}
              </p>
            </div>
            <span
              className={`rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                item.enrichment_status === 'complete'
                  ? 'bg-emerald-50 text-emerald-700'
                  : item.enrichment_status === 'failed'
                    ? 'bg-red-50 text-red-700'
                    : 'bg-amber-50 text-amber-700'
              }`}
            >
              {item.enrichment_status || t('media_pending', 'Pending')}
            </span>
          </div>
          <div className="mt-3 grid gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => enrichMutation.mutate(false)}
                disabled={enrichMutation.isPending}
                className="inline-flex h-8 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-card px-3 text-[11px] font-semibold text-n-text transition hover:bg-white disabled:opacity-50"
              >
                {enrichMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-n-green" />}
                {t('media_enrich_asset', 'Enrich asset')}
              </button>
              <button
                type="button"
                onClick={() => enrichMutation.mutate(true)}
                disabled={enrichMutation.isPending}
                className="inline-flex h-8 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-card px-3 text-[11px] font-semibold text-n-text transition hover:bg-white disabled:opacity-50"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {t('media_regenerate_alt_text', 'Regenerate alt text')}
              </button>
              <button
                type="button"
                onClick={() => captionMutation.mutate()}
                disabled={captionMutation.isPending}
                className="inline-flex h-8 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-card px-3 text-[11px] font-semibold text-n-text transition hover:bg-white disabled:opacity-50"
              >
                {captionMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                {t('media_refresh_captions', 'Refresh captions')}
              </button>
            </div>
            <div className="rounded-n-sm border border-n-border bg-n-card p-3">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={variantPlatform}
                  onChange={(e) => setVariantPlatform(e.target.value)}
                  className="h-8 rounded-n-sm border border-n-border bg-white px-2.5 text-[11px] font-medium text-n-text"
                >
                  {['instagram', 'facebook', 'linkedin', 'pinterest', 'youtube', 'twitter', 'tiktok'].map((platform) => (
                    <option key={platform} value={platform}>
                      {platform}
                    </option>
                  ))}
                </select>
                <select
                  value={variantStrategy}
                  onChange={(e) => setVariantStrategy(e.target.value as 'pad' | 'crop' | 'trim')}
                  className="h-8 rounded-n-sm border border-n-border bg-white px-2.5 text-[11px] font-medium text-n-text"
                >
                  <option value="pad">{t('media_variant_strategy_pad', 'Pad')}</option>
                  <option value="crop">{t('media_variant_strategy_crop', 'Crop')}</option>
                  <option value="trim">{t('media_variant_strategy_trim', 'Trim')}</option>
                </select>
                <button
                  type="button"
                  onClick={() => platformReadyMutation.mutate()}
                  disabled={platformReadyMutation.isPending}
                  className="inline-flex h-8 items-center gap-1.5 rounded-n-sm bg-n-green px-3 text-[11px] font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                >
                  {platformReadyMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  )}
                  {t('media_make_platform_ready', 'Make platform ready')}
                </button>
              </div>
            </div>
            {item.enrichment_error && (
              <div className="rounded-n-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {item.enrichment_error}
              </div>
            )}
          </div>
        </div>

        <div className="mb-4 rounded-n border border-n-border bg-n-hover p-3">
          <h4 className="text-xs font-medium text-n-text-md uppercase tracking-wider">
            {t('media_source', 'Source')}
          </h4>
          <div className="mt-2 space-y-2 text-sm text-n-text-md">
            <div className="flex items-center justify-between gap-3">
              <span>{t('media_source_type', 'Type')}</span>
              <span className="font-medium text-n-text">{sourceLabel}</span>
            </div>
            {sourceProvider && (
              <div className="flex items-center justify-between gap-3">
                <span>{t('media_source_provider', 'Provider')}</span>
                <span className="font-medium text-n-text">{sourceProvider}</span>
              </div>
            )}
            {item.source_url && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-n-text-lt">
                  {t('media_source_link', 'Original link')}
                </p>
                <a
                  href={item.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="line-clamp-2 break-all text-xs text-n-green hover:underline"
                >
                  {item.source_url}
                </a>
              </div>
            )}
            {item.ai_description && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-n-text-lt">
                  {t('media_ai_description', 'AI description')}
                </p>
                <p className="text-sm leading-relaxed text-n-text">{item.ai_description}</p>
              </div>
            )}
            {item.provenance_model && (
              <div className="grid grid-cols-2 gap-2 rounded-n-sm border border-n-border bg-n-card px-3 py-2 text-xs">
                <div>
                  <p className="uppercase tracking-wider text-n-text-lt">{t('media_provenance_model', 'Model')}</p>
                  <p className="mt-1 font-medium text-n-text">{item.provenance_model}</p>
                </div>
                <div>
                  <p className="uppercase tracking-wider text-n-text-lt">{t('media_provenance_provider', 'Provider')}</p>
                  <p className="mt-1 font-medium text-n-text">{item.provenance_provider || '—'}</p>
                </div>
              </div>
            )}
            {item.provenance_prompt && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-n-text-lt">
                  {t('media_generation_prompt', 'Prompt / provenance')}
                </p>
                <p className="text-sm leading-relaxed text-n-text">{item.provenance_prompt}</p>
              </div>
            )}
          </div>
        </div>

        <div className="mb-4 rounded-n border border-n-border bg-n-hover p-3">
          <h4 className="text-xs font-medium uppercase tracking-wider text-n-text-md">
            {t('media_enrichment_details', 'Enrichment details')}
          </h4>
          <div className="mt-2 space-y-3 text-sm text-n-text-md">
            {item.visual_description && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-n-text-lt">
                  {t('media_visual_description', 'Visual description')}
                </p>
                <p className="leading-relaxed text-n-text">{item.visual_description}</p>
              </div>
            )}
            {captionSuggestions.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wider text-n-text-lt">
                  {t('media_caption_suggestions', 'Caption suggestions')}
                </p>
                <div className="space-y-2">
                  {captionSuggestions.slice(0, 3).map((caption, idx) => (
                    <button
                      key={`${caption}-${idx}`}
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(caption);
                        toast.success(t('media_caption_copied', 'Caption copied'));
                      }}
                      className="w-full rounded-n-sm border border-n-border bg-n-card px-3 py-2 text-left text-xs leading-5 text-n-text transition hover:bg-white"
                    >
                      {caption}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {smartTags.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-n-text-lt">
                  {t('media_smart_tags', 'Smart tags')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {smartTags.map((tag) => (
                    <span key={tag} className="rounded-full bg-n-card px-2 py-0.5 text-[11px] font-medium text-n-text">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {detectedObjects.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-n-text-lt">
                  {t('media_detected_objects', 'Detected objects')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {detectedObjects.map((name) => (
                    <span key={name} className="rounded-full bg-n-card px-2 py-0.5 text-[11px] font-medium text-n-text">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {!item.visual_description &&
              captionSuggestions.length === 0 &&
              smartTags.length === 0 &&
              detectedObjects.length === 0 && (
                <p className="text-sm text-n-text-md">
                  {t('media_enrichment_empty', 'No enrichment details yet. Run Enrich asset to fill this in.')}
                </p>
              )}
          </div>
        </div>

        <div className="mb-4 rounded-n border border-n-border bg-n-hover p-3">
          <h4 className="text-xs font-medium text-n-text-md uppercase tracking-wider">
            {t('media_used_in', 'Used in')}
          </h4>
          <div className="mt-2 space-y-3 text-sm text-n-text-md">
            {isUsageLoading ? (
              <div className="flex items-center gap-2 text-n-text-md">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('media_usage_loading', 'Checking posts and pages...')}
              </div>
            ) : usageData && (usageData.post_count > 0 || usageData.page_count > 0) ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-n-sm border border-n-border bg-n-card px-3 py-2">
                    <p className="text-xs uppercase tracking-wider text-n-text-lt">
                      {t('media_usage_posts', 'Posts')}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-n-text">{usageData.post_count}</p>
                  </div>
                  <div className="rounded-n-sm border border-n-border bg-n-card px-3 py-2">
                    <p className="text-xs uppercase tracking-wider text-n-text-lt">
                      {t('media_usage_pages', 'Pages')}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-n-text">{usageData.page_count}</p>
                  </div>
                </div>
                {usageData.posts.length > 0 && (
                  <div className="space-y-2">
                    {usageData.posts.slice(0, 3).map((post) => (
                      <button
                        key={post.id}
                        type="button"
                        onClick={() => navigate(`/compose/${post.id}`)}
                        className="w-full rounded-n-sm border border-n-border bg-n-card px-3 py-2 text-left transition hover:bg-white"
                      >
                        <p className="truncate font-medium text-n-text">{post.title}</p>
                        <p className="mt-1 text-xs text-n-text-md">
                          {post.status}
                          {post.platforms.length ? ` • ${post.platforms.join(', ')}` : ''}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
                {usageData.pages.length > 0 && (
                  <div className="space-y-2">
                    {usageData.pages.slice(0, 3).map((page) => (
                      <button
                        key={page.id}
                        type="button"
                        onClick={() => navigate(`/builder/${page.id}`)}
                        className="w-full rounded-n-sm border border-n-border bg-n-card px-3 py-2 text-left transition hover:bg-white"
                      >
                        <p className="truncate font-medium text-n-text">{page.title}</p>
                        <p className="mt-1 text-xs text-n-text-md">
                          /{page.slug}
                          {page.is_published ? ` • ${t('media_live', 'Live')}` : ` • ${t('media_draft', 'Draft')}`}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-n-text-md">
                {t('media_usage_empty', 'This asset is not linked to any posts or pages yet.')}
              </p>
            )}
          </div>
        </div>

        <div className="mb-4 rounded-n border border-n-border bg-n-hover p-3">
          <h4 className="text-xs font-medium text-n-text-md uppercase tracking-wider">
            {t('media_versions', 'Versions')}
          </h4>
          <div className="mt-2 space-y-2">
            {versionEntries.length > 0 ? (
              versionEntries.map(([name, version]) => (
                <div
                  key={name}
                  className="rounded-n-sm border border-n-border bg-n-card px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-n-text">{name.replace(/_/g, ' ')}</p>
                    <span className="text-xs text-n-text-md">
                      {version.width && version.height ? `${version.width}×${version.height}` : t('media_version_ready', 'Ready')}
                    </span>
                  </div>
                  {version.url && (
                    <a
                      href={version.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex text-xs text-n-green hover:underline"
                    >
                      {t('media_open_version', 'Open version')}
                    </a>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-n-text-md">
                {t('media_versions_empty', 'No derived variants have been generated yet.')}
              </p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="text-xs font-medium text-n-text-md uppercase tracking-wider">
            {t('media_internal_notes', 'Internal notes')}
          </h4>
          {editingNotes ? (
            <div className="mt-2">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full resize-none rounded-n border border-gray-300 px-3 py-2 text-sm"
                rows={4}
                placeholder={t('media_internal_notes_placeholder', 'Add notes for your team, campaign context, or usage guidance.')}
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => {
                    onUpdate({ internal_notes: notes });
                    setEditingNotes(false);
                  }}
                  className="inline-flex items-center gap-1 rounded-n bg-[#2383e2] px-3 py-1.5 text-xs font-semibold text-white"
                >
                  {isUpdating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  {t('media_save', 'Save')}
                </button>
                <button
                  onClick={() => {
                    setNotes(item.internal_notes || '');
                    setEditingNotes(false);
                  }}
                  className="rounded-n px-3 py-1.5 text-xs font-medium text-n-text-md hover:bg-n-page"
                >
                  {t('media_cancel', 'Cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-2 flex items-start gap-2">
              <p className="flex-1 text-sm leading-relaxed text-n-text-md">
                {item.internal_notes || (
                  <span className="italic text-n-text-lt">
                    {t('media_internal_notes_empty', 'No internal notes yet.')}
                  </span>
                )}
              </p>
              <button
                onClick={() => setEditingNotes(true)}
                className="p-1 text-n-text-lt transition hover:bg-n-page hover:text-n-text"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-2 pt-2 border-t border-n-border">
          <button
            onClick={onCreatePostFromMedia}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-text hover:bg-n-hover rounded-n-sm transition"
          >
            <Sparkles className="w-4 h-4" /> {t('media_create_post_from_media', 'Create post from media')}
          </button>
          <button
            onClick={onOpenInStudio}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-text hover:bg-n-hover rounded-n-sm transition"
          >
            <Edit2 className="w-4 h-4" /> {t('media_open_in_studio', 'Open in AI Studio')}
          </button>
          <button onClick={copyUrl}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-text hover:bg-n-hover rounded-n-sm transition">
            <Copy className="w-4 h-4" /> {t('media_copy_url', 'Copy URL')}
          </button>
          {item.media_type === 'image' && (
            <button onClick={() => navigate(`/editor?mediaId=${encodeURIComponent(item.id)}&returnTo=/media&source=media`)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-text hover:bg-n-hover rounded-n-sm transition">
              <Edit2 className="w-4 h-4" /> {t('media_edit_in_editor', 'Edit in Image Editor')}
            </button>
          )}
          {item.media_type === 'video' && (
            <button onClick={() => navigate(`/video-editor?mediaId=${encodeURIComponent(item.id)}&returnTo=/media&source=media`)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-text hover:bg-n-hover rounded-n-sm transition">
              <Edit2 className="w-4 h-4" /> {t('media_edit_in_video_editor', 'Edit in Video Editor')}
            </button>
          )}
          {item.media_type === 'video' && onTrimVideo && (
            <button onClick={onTrimVideo}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-text hover:bg-n-hover rounded-n-sm transition">
              <Edit2 className="w-4 h-4" /> {t('videotrim_open_modal', 'Trim Video')}
            </button>
          )}
          <a href={item.url} target="_blank" rel="noopener noreferrer"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-text hover:bg-n-hover rounded-n-sm transition">
            <ExternalLink className="w-4 h-4" /> {t('media_open_original', 'Open Original')}
          </a>
          <button
            onClick={onArchive}
            disabled={isArchiving}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-text hover:bg-n-hover rounded-n-sm transition disabled:opacity-50"
          >
            {isArchiving ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            {t('media_archive', 'Archive asset')}
          </button>
          <button onClick={onDelete} disabled={isDeleting}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-n-sm transition disabled:opacity-50">
            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {t('media_delete', 'Delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
export default function MediaLibraryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showVideoTrimmer, setShowVideoTrimmer] = useState(false);
  const [needsAltOnly, setNeedsAltOnly] = useState(false);
  const [workspacePanel, setWorkspacePanel] = useState<
    'cloud' | 'imports' | 'listening' | 'submissions' | null
  >(null);
  const [activeCloudAccountId, setActiveCloudAccountId] = useState<string | null>(null);
  const [cloudSearchQuery, setCloudSearchQuery] = useState('');
  const [cloudFiles, setCloudFiles] = useState<CloudFileLite[]>([]);
  const [cloudNextPage, setCloudNextPage] = useState<string | null>(null);
  const [cloudIsLoading, setCloudIsLoading] = useState(false);
  const [isConnectingCloud, setIsConnectingCloud] = useState(false);
  const [urlImportValue, setUrlImportValue] = useState('');
  const [urlImportFilename, setUrlImportFilename] = useState('');
  const [rssUrl, setRssUrl] = useState('');
  const [rssFeedMeta, setRssFeedMeta] = useState<Record<string, unknown> | null>(null);
  const [rssDiscovery, setRssDiscovery] = useState<RssDiscoveryItem[]>([]);
  const [selectedRssImages, setSelectedRssImages] = useState<string[]>([]);
  const [listeningPlatform, setListeningPlatform] = useState('all');
  const [listeningDays, setListeningDays] = useState(30);
  const [selectedMentionIds, setSelectedMentionIds] = useState<string[]>([]);
  const [submissionReviewNotes, setSubmissionReviewNotes] = useState<Record<string, string>>({});
  const [submissionLink, setSubmissionLink] = useState<string | null>(null);
  const LIMIT = 24;

  useEffect(() => {
    const newMediaId = searchParams.get('newMediaId');
    if (!newMediaId) {
      return;
    }
    setSelectedId(newMediaId);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('newMediaId');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data?.type === 'cloud-storage-connected') {
        setIsConnectingCloud(false);
        queryClient.invalidateQueries({ queryKey: ['cloud-storage-accounts'] });
        toast.success(
          t('media_cloud_connected', 'Cloud account connected successfully.'),
        );
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [queryClient, t]);

  const handleSearch = (val: string) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 300);
  };

  const refreshWorkspace = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['media-library'] });
    queryClient.invalidateQueries({ queryKey: ['media-stats'] });
  }, [queryClient]);

  const { data: cloudAccountsData } = useQuery({
    queryKey: ['cloud-storage-accounts'],
    queryFn: async () => {
      const resp = await cloudStorageAPI.listAccounts();
      return (resp.data?.accounts || []) as CloudAccountLite[];
    },
    staleTime: 60_000,
  });

  const { data: submissionsData, refetch: refetchSubmissions } = useQuery({
    queryKey: ['media-submissions'],
    queryFn: async () => {
      const resp = await mediaAPI.listSubmissions();
      return (resp.data?.submissions || []) as MediaSubmissionRecord[];
    },
    enabled: workspacePanel === 'submissions',
    staleTime: 30_000,
  });

  const { data: listeningMentionsData, isFetching: isFetchingListening } = useQuery({
    queryKey: ['media-listening-import', listeningPlatform, listeningDays],
    queryFn: async () => {
      const resp = await listeningAPI.getMentions({
        platform: listeningPlatform === 'all' ? undefined : listeningPlatform,
        days: listeningDays,
        limit: 30,
        page: 1,
      });
      return (resp.data?.mentions || []) as ListeningMentionLite[];
    },
    enabled: workspacePanel === 'listening',
    staleTime: 30_000,
  });

  const connectCloudProvider = useCallback(
    async (provider: 'google_drive' | 'dropbox') => {
      if (isConnectingCloud) {
        return;
      }
      setIsConnectingCloud(true);
      try {
        const resp = await cloudStorageAPI.startOAuth(provider);
        window.open(resp.data.auth_url, '_blank', 'width=700,height=760');
        window.setTimeout(() => setIsConnectingCloud(false), 60_000);
      } catch (error) {
        setIsConnectingCloud(false);
        toast.error(
          getErrorMessage(
            error,
            t('media_cloud_connect_failed', 'Failed to start cloud connection.'),
          ),
        );
      }
    },
    [isConnectingCloud, t],
  );

  const browseCloudFiles = useCallback(
    async (accountId: string, searchQuery?: string, pageToken?: string | null) => {
      setCloudIsLoading(true);
      setActiveCloudAccountId(accountId);
      try {
        const resp = await cloudStorageAPI.browse(accountId, {
          query: searchQuery || undefined,
          page_token: pageToken || undefined,
        });
        const incoming = (resp.data?.files || []).map((file: Record<string, unknown>) => ({
          id: String(file.id || ''),
          name: String(file.name || 'Untitled'),
          mime_type: String(file.mime_type || ''),
          type: String(file.type || 'image'),
          thumbnail_url: typeof file.thumbnail === 'string' ? file.thumbnail : null,
          size: typeof file.size === 'number' ? file.size : null,
        })) as CloudFileLite[];
        setCloudFiles((prev) => (pageToken ? [...prev, ...incoming] : incoming));
        setCloudNextPage(resp.data?.next_page_token || null);
      } catch (error) {
        toast.error(
          getErrorMessage(error, t('media_cloud_browse_failed', 'Failed to load cloud files.')),
        );
      } finally {
        setCloudIsLoading(false);
      }
    },
    [t],
  );

  // Fetch media list
  const { data: mediaData, isLoading } = useQuery({
    queryKey: ['media-library', typeFilter, debouncedSearch, page],
    queryFn: async () => {
      const params: { page: number; limit: number; media_type?: string; search?: string } = { page, limit: LIMIT };
      if (typeFilter !== 'all') params.media_type = typeFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await mediaAPI.list(params);
      return data as { media: MediaItem[]; total: number; page: number };
    },
    staleTime: 30_000,
    gcTime: 120_000,     // free cache after 2 min unmounted
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['media-stats'],
    queryFn: async () => {
      const { data } = await mediaAPI.stats();
      return data as MediaStats;
    },
    staleTime: 60_000,
    gcTime: 120_000,
  });

  // Per-file upload progress tracking
  interface UploadFileState {
    name: string;
    size: number;
    loaded: number;
    status: 'pending' | 'uploading' | 'completed' | 'error';
    error?: string;
  }
  const [uploadFiles, setUploadFiles] = useState<UploadFileState[]>([]);

  const updateFileProgress = useCallback((index: number, updates: Partial<UploadFileState>) => {
    setUploadFiles(prev => prev.map((f, i) => i === index ? { ...f, ...updates } : f));
  }, []);

  // Upload mutation with per-file progress
  const uploadMutation = useMutation({
    mutationFn: async (files: FileList) => {
      const fileArray = Array.from(files);
      const initialStates: UploadFileState[] = fileArray.map(f => ({
        name: f.name, size: f.size, loaded: 0, status: 'pending' as const,
      }));
      setUploadFiles(initialStates);

      const results = [];
      for (let i = 0; i < fileArray.length; i++) {
        updateFileProgress(i, { status: 'uploading' });
        try {
          const onProgress: UploadProgressCallback = (loaded, total) => {
            updateFileProgress(i, { loaded, size: total });
          };
          const resp = await mediaAPI.directUpload(fileArray[i], onProgress);
          updateFileProgress(i, { status: 'completed', loaded: fileArray[i].size });
          results.push(resp.data);
        } catch (err) {
          const error = err as { response?: { data?: { detail?: string | { error: string; used_mb: number; limit_mb: number; plan: string } } } };
          const detail = error?.response?.data?.detail;
          let msg = 'Upload failed';
          if (detail && typeof detail === 'object' && detail.error === 'storage_quota_exceeded') {
            msg = `Storage full: ${detail.used_mb}MB of ${detail.limit_mb}MB used`;
          } else if (typeof detail === 'string') {
            msg = detail;
          }
          updateFileProgress(i, { status: 'error', error: msg });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      const succeeded = results.filter(Boolean).length;
      if (succeeded > 0) {
        toast.success(t('media_files_uploaded', '{{count}} file{{plural}} uploaded', { count: succeeded, plural: succeeded > 1 ? 's' : '' }));
      }
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media-stats'] });
      // Clear progress after 3 seconds
      setTimeout(() => setUploadFiles([]), 3000);
    },
    onError: (err) => {
      const error = err as { response?: { data?: { detail?: string | { error: string; used_mb: number; limit_mb: number; plan: string } } } };
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'storage_quota_exceeded') {
        toast.error(t('media_storage_full', `Storage full: ${detail.used_mb}MB of ${detail.limit_mb}MB used (${detail.plan} plan). Upgrade to upload more.`));
      } else {
        toast.error(typeof detail === 'string' ? detail : t('media_upload_failed', 'Upload failed'));
      }
      setTimeout(() => setUploadFiles([]), 3000);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => mediaAPI.delete(id),
    onSuccess: () => {
      toast.success(t('media_deleted', 'Media deleted'));
      setSelectedId(null);
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media-stats'] });
    },
    onError: () => toast.error(t('media_delete_failed', 'Failed to delete')),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) =>
      mediaAPI.bulkUpdate({
        media_ids: [id],
        archived: true,
      }),
    onSuccess: () => {
      toast.success(t('media_archived', 'Asset archived'));
      setSelectedId(null);
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media-stats'] });
    },
    onError: () => toast.error(t('media_archive_failed', 'Failed to archive asset')),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: MediaUpdatePayload }) => mediaAPI.update(id, data),
    onSuccess: () => {
      toast.success(t('media_updated', 'Updated'));
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
    },
    onError: () => toast.error(t('media_update_failed', 'Failed to update')),
  });

  const bulkAltTextMutation = useMutation({
    mutationFn: (payload: { media_ids: string[]; overwrite_alt_text?: boolean }) =>
      mediaAPI.bulkAltText(payload),
    onSuccess: (response) => {
      const count = response.data?.count || 0;
      toast.success(
        t('media_bulk_alt_success', 'Generated alt text for {{count}} assets.', { count }),
      );
      refreshWorkspace();
      setSelectedIds([]);
    },
    onError: (error) =>
      toast.error(getErrorMessage(error, t('media_bulk_alt_failed', 'Failed to generate alt text.'))),
  });

  const quickEnrichMutation = useMutation({
    mutationFn: (mediaId: string) => mediaAPI.enrich(mediaId, { overwrite_alt_text: false }),
    onSuccess: () => {
      toast.success(t('media_enrich_success', 'Asset enriched.'));
      refreshWorkspace();
    },
    onError: (error) =>
      toast.error(getErrorMessage(error, t('media_enrich_failed', 'Failed to enrich asset.'))),
  });

  const quickPlatformReadyMutation = useMutation({
    mutationFn: (mediaId: string) =>
      mediaAPI.makePlatformReady(mediaId, {
        target_platforms: ['instagram'],
        content_type: 'post',
        strategy: 'pad',
      }),
    onSuccess: () => {
      toast.success(t('media_platform_ready_quick_success', 'Instagram-ready variant refreshed.'));
      refreshWorkspace();
    },
    onError: (error) =>
      toast.error(
        getErrorMessage(
          error,
          t('media_platform_ready_quick_failed', 'Failed to build the platform-ready variant.')
        )
      ),
  });

  const urlImportMutation = useMutation({
    mutationFn: (payload: { url: string; filename?: string }) => mediaAPI.importUrl(payload),
    onSuccess: (response) => {
      const created = response.data?.media;
      toast.success(t('media_import_url_success', 'Asset imported from URL.'));
      refreshWorkspace();
      if (created?.id) {
        setSelectedId(created.id);
      }
      setUrlImportValue('');
      setUrlImportFilename('');
    },
    onError: (error) =>
      toast.error(getErrorMessage(error, t('media_import_url_failed', 'Failed to import URL.'))),
  });

  const discoverRssMutation = useMutation({
    mutationFn: (payload: { url: string }) => mediaAPI.discoverRssImages(payload),
    onSuccess: (response) => {
      setRssFeedMeta(response.data?.feed || null);
      setRssDiscovery((response.data?.items || []) as RssDiscoveryItem[]);
      setSelectedRssImages([]);
      toast.success(
        t('media_rss_discovered', 'Feed scanned. Select the images you want to import.'),
      );
    },
    onError: (error) =>
      toast.error(getErrorMessage(error, t('media_rss_discover_failed', 'Failed to read RSS feed.'))),
  });

  const importRssMutation = useMutation({
    mutationFn: (payload: { feed_url: string; items: RssDiscoveryItem[] }) =>
      mediaAPI.importRssImages({
        feed_url: payload.feed_url,
        items: payload.items.map((item) => ({
          image_url: item.image_url,
          title: item.title ?? null,
          item_url: item.item_url ?? null,
          pub_date: item.pub_date ?? null,
          author: item.author ?? null,
        })),
      }),
    onSuccess: (response) => {
      const count = response.data?.count || 0;
      toast.success(
        t('media_rss_import_success', 'Imported {{count}} images from the feed.', { count }),
      );
      refreshWorkspace();
      setSelectedRssImages([]);
    },
    onError: (error) =>
      toast.error(getErrorMessage(error, t('media_rss_import_failed', 'Failed to import feed images.'))),
  });

  const importListeningMutation = useMutation({
    mutationFn: (payload: { mention_ids: string[] }) => mediaAPI.importListeningMedia(payload),
    onSuccess: (response) => {
      const count = response.data?.count || 0;
      toast.success(
        t('media_listening_import_success', 'Imported {{count}} listening assets.', { count }),
      );
      refreshWorkspace();
      setSelectedMentionIds([]);
    },
    onError: (error) =>
      toast.error(
        getErrorMessage(error, t('media_listening_import_failed', 'Failed to import listening assets.')),
      ),
  });

  const createSubmissionLinkMutation = useMutation({
    mutationFn: () => mediaAPI.createSubmissionLink({ expires_days: 14 }),
    onSuccess: (response) => {
      setSubmissionLink(response.data?.public_url || null);
      toast.success(t('media_submission_link_success', 'Submission link created.'));
    },
    onError: (error) =>
      toast.error(
        getErrorMessage(error, t('media_submission_link_failed', 'Failed to create submission link.')),
      ),
  });

  const approveSubmissionMutation = useMutation({
    mutationFn: ({ submissionId, reviewNotes }: { submissionId: string; reviewNotes?: string }) =>
      mediaAPI.approveSubmission(submissionId, { review_notes: reviewNotes }),
    onSuccess: (response) => {
      toast.success(t('media_submission_approved', 'Submission approved and added to the library.'));
      refreshWorkspace();
      refetchSubmissions();
      const created = response.data?.media;
      if (created?.id) {
        setSelectedId(created.id);
      }
    },
    onError: (error) =>
      toast.error(
        getErrorMessage(error, t('media_submission_approve_failed', 'Failed to approve submission.')),
      ),
  });

  const rejectSubmissionMutation = useMutation({
    mutationFn: ({ submissionId, reviewNotes }: { submissionId: string; reviewNotes?: string }) =>
      mediaAPI.rejectSubmission(submissionId, { review_notes: reviewNotes }),
    onSuccess: () => {
      toast.success(t('media_submission_rejected', 'Submission rejected.'));
      refetchSubmissions();
    },
    onError: (error) =>
      toast.error(
        getErrorMessage(error, t('media_submission_reject_failed', 'Failed to reject submission.')),
      ),
  });

  const cloudImportMutation = useMutation({
    mutationFn: ({ accountId, fileId }: { accountId: string; fileId: string }) =>
      cloudStorageAPI.importFile(accountId, fileId),
    onSuccess: (response) => {
      toast.success(t('media_cloud_import_success', 'Cloud asset imported.'));
      refreshWorkspace();
      const createdId = response.data?.media_id;
      if (createdId) {
        setSelectedId(createdId);
      }
    },
    onError: (error) =>
      toast.error(
        getErrorMessage(error, t('media_cloud_import_failed', 'Failed to import from cloud.')),
      ),
  });

  const items = useMemo(() => mediaData?.media || [], [mediaData?.media]);
  const total = mediaData?.total || 0;
  const totalPages = Math.ceil(total / LIMIT);
  const displayedItems = useMemo(
    () => (needsAltOnly ? items.filter((item) => !item.alt_text) : items),
    [items, needsAltOnly],
  );
  const selectedItem = items.find((i) => i.id === selectedId);
  const visibleNeedsAltCount = items.filter((item) => !item.alt_text).length;
  const cloudAccounts = cloudAccountsData || [];
  const listeningMentions = listeningMentionsData || [];
  const submissions = submissionsData || [];
  const selectedMediaCount = selectedIds.length;
  const selectedRssItems = rssDiscovery.filter((item) =>
    selectedRssImages.includes(item.image_url),
  );

  useEffect(() => {
    const currentIds = new Set(displayedItems.map((item) => item.id));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => currentIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [displayedItems]);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  }, []);

  const toggleRssSelection = useCallback((imageUrl: string) => {
    setSelectedRssImages((prev) =>
      prev.includes(imageUrl)
        ? prev.filter((value) => value !== imageUrl)
        : [...prev, imageUrl],
    );
  }, []);

  const toggleMentionSelection = useCallback((mentionId: string) => {
    setSelectedMentionIds((prev) =>
      prev.includes(mentionId)
        ? prev.filter((value) => value !== mentionId)
        : [...prev, mentionId],
    );
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      uploadMutation.mutate(e.dataTransfer.files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uploadMutation is a stable mutation object; including it would cause unnecessary re-renders
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => e.preventDefault(), []);

  const TYPE_FILTERS = [
    { key: 'all' as TypeFilter, label: t('media_all', 'All'), icon: <Grid className="w-3.5 h-3.5" /> },
    { key: 'image' as TypeFilter, label: t('media_images', 'Images'), icon: <Image className="w-3.5 h-3.5" /> },
    { key: 'video' as TypeFilter, label: t('media_videos', 'Videos'), icon: <Video className="w-3.5 h-3.5" /> },
  ];

  const WORKSPACE_ACTIONS = [
    {
      key: 'cloud' as const,
      label: t('media_workspace_cloud', 'Drive / Dropbox'),
      icon: <Cloud className="h-3.5 w-3.5" />,
    },
    {
      key: 'imports' as const,
      label: t('media_workspace_imports', 'URL / RSS'),
      icon: <Link2 className="h-3.5 w-3.5" />,
    },
    {
      key: 'listening' as const,
      label: t('media_workspace_listening', 'Import from listening'),
      icon: <Radio className="h-3.5 w-3.5" />,
    },
    {
      key: 'submissions' as const,
      label: t('media_workspace_submissions', 'Creator submissions'),
      icon: <Inbox className="h-3.5 w-3.5" />,
    },
  ];

  const headerStats = [
    {
      label: t('media_stat_total', 'Assets'),
      value: stats?.total ?? total,
    },
    {
      label: t('media_stat_storage', 'Storage'),
      value: stats ? `${stats.quota.used_mb} MB` : '—',
    },
    {
      label: t('media_stat_images', 'Images'),
      value: stats?.by_type.image ?? 0,
    },
    {
      label: t('media_stat_needs_alt', 'Needs alt text'),
      value: visibleNeedsAltCount,
    },
  ];

  const pendingSubmissionsCount = submissions.filter(
    (submission) => submission.status === 'pending',
  ).length;

  return (
    <div className="flex h-full bg-n-page" onDrop={handleDrop} onDragOver={handleDragOver}>
      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="border-b border-n-border bg-n-card px-5 py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-n-green/20 bg-n-green-bg px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-n-green">
                <Sparkles className="h-3 w-3" />
                {t('media_workspace_badge', 'Asset workspace')}
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-n-text md:text-[2rem]">
                {t('media_page_title', 'Media Library')}
              </h1>
              <p className="mt-1 max-w-xl text-[13px] leading-5 text-n-text-md">
                {t(
                  'media_page_subtitle',
                  'Upload, organize, and manage reusable images and videos for posts, pages, and AI Studio.',
                )}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {headerStats.map(({ label, value }) => (
                  <div
                    key={label}
                    className="rounded-n-sm border border-n-border bg-n-page px-3 py-2"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                      {label}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-n-text">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/ai-images')}
                className="inline-flex h-8 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-card px-3 text-xs font-semibold text-n-text transition-colors hover:bg-n-hover"
              >
                <Sparkles className="h-3.5 w-3.5 text-n-green" />
                {t('media_generate_with_ai', 'Generate with AI')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/editor')}
                className="inline-flex h-8 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-card px-3 text-xs font-semibold text-n-text transition-colors hover:bg-n-hover"
              >
                <Edit2 className="h-3.5 w-3.5" />
                {t('media_open_editor', 'Open editor')}
              </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploadMutation.isPending}
                className="inline-flex h-8 items-center gap-1.5 rounded-n-sm bg-n-green px-3.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {uploadMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              {t('media_upload', 'Upload')}
            </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => e.target.files && uploadMutation.mutate(e.target.files)}
            />
          </div>
        </div>

        {/* Action Bar */}
        <div className="border-b border-n-border bg-n-card px-5 py-3">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {WORKSPACE_ACTIONS.map((action) => (
              <button
                key={action.key}
                type="button"
                onClick={() =>
                  setWorkspacePanel((current) =>
                    current === action.key ? null : action.key,
                  )
                }
                className={`inline-flex h-8 items-center gap-1.5 rounded-n-sm border px-3 text-[11px] font-semibold transition ${
                  workspacePanel === action.key
                    ? 'border-n-green/30 bg-n-green-bg text-n-green'
                    : 'border-n-border bg-n-card text-n-text-md hover:bg-n-hover hover:text-n-text'
                }`}
              >
                {action.icon}
                {action.label}
                {action.key === 'submissions' && pendingSubmissionsCount > 0 ? (
                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                    {pendingSubmissionsCount}
                  </span>
                ) : null}
              </button>
            ))}

            {selectedMediaCount > 0 && (
              <button
                type="button"
                onClick={() =>
                  bulkAltTextMutation.mutate({
                    media_ids: selectedIds,
                    overwrite_alt_text: false,
                  })
                }
                disabled={bulkAltTextMutation.isPending}
                className="inline-flex h-8 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-page px-3 text-[11px] font-semibold text-n-text transition-colors hover:bg-n-hover disabled:opacity-60"
              >
                {bulkAltTextMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Wand2 className="h-3.5 w-3.5 text-n-green" />
                )}
                {t('media_bulk_alt_button', 'Generate alt text')}
                <span className="text-n-text-lt">{selectedMediaCount}</span>
              </button>
            )}
          </div>

          {/* Upload progress panel */}
          {uploadFiles.length > 0 && (
            <div className="mt-3 bg-n-hover rounded-n-sm border border-n-border p-3 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-n-text">
                  {t('media_uploading', 'Uploading {{completed}}/{{total}} files', { completed: uploadFiles.filter(f => f.status === 'completed').length, total: uploadFiles.length })}
                </p>
                {!uploadMutation.isPending && (
                  <button onClick={() => setUploadFiles([])} className="text-n-text-lt hover:text-n-text-md">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {uploadFiles.map((uf, idx) => {
                const pct = uf.size > 0 ? Math.round((uf.loaded / uf.size) * 100) : 0;
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <p className="text-[11px] text-n-text-md truncate">{uf.name}</p>
                        <span className={`text-[10px] font-medium ${
                          uf.status === 'completed' ? 'text-emerald-600' :
                          uf.status === 'error' ? 'text-red-500' :
                          uf.status === 'uploading' ? 'text-blue-600' : 'text-n-text-lt'
                        }`}>
                          {uf.status === 'completed' ? `Done · ${formatBytes(uf.size)}` :
                           uf.status === 'error' ? uf.error || 'Failed' :
                           uf.status === 'uploading' ? `${formatBytes(uf.loaded)} / ${formatBytes(uf.size)}` :
                           formatBytes(uf.size)}
                        </span>
                      </div>
                      <div className="w-full bg-n-border rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${
                            uf.status === 'completed' ? 'bg-emerald-500' :
                            uf.status === 'error' ? 'bg-red-400' : 'bg-blue-500'
                          }`}
                          style={{ width: `${uf.status === 'completed' ? 100 : pct}%` }}
                        />
                      </div>
                    </div>
                    {uf.status === 'completed' && <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                    {uf.status === 'error' && <X className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* Search + Filters */}
          <div className="flex flex-col gap-2.5 xl:flex-row xl:items-center xl:justify-between">
            <div className="relative w-full xl:max-w-md">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-n-text-lt" />
              <input
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={t('media_search_placeholder', 'Search files...')}
                aria-label={t('media_search_aria', 'Search files')}
                className="h-9 w-full rounded-n-sm border border-n-border bg-n-card pl-9 pr-4 text-[13px] text-n-text outline-none transition-colors placeholder:text-n-text-lt focus:border-n-green focus:ring-2 focus:ring-n-green/10"
                style={{ '--tw-ring-color': '#2383e2' } as React.CSSProperties}
              />
              {search && (
                <button onClick={() => { setSearch(''); setDebouncedSearch(''); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-n-text-lt hover:text-n-text-md">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-n-sm border border-n-border bg-n-page p-0.5">
                {TYPE_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => { setTypeFilter(f.key); setNeedsAltOnly(false); setPage(1); }}
                    className={`flex h-7.5 items-center gap-1.5 rounded-[9px] px-2.5 text-[11px] font-semibold transition ${
                      typeFilter === f.key ? 'bg-n-card text-n-text shadow-sm' : 'text-n-text-md hover:text-n-text'
                    }`}
                  >
                    {f.icon}
                    {f.label}
                    {stats && f.key !== 'all' && stats.by_type[f.key] && (
                      <span className="ml-0.5 text-n-text-lt">{stats.by_type[f.key]}</span>
                    )}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setNeedsAltOnly((prev) => !prev)}
                className={`flex h-8 items-center gap-1.5 rounded-n-sm border px-2.5 text-[11px] font-semibold transition ${
                  needsAltOnly
                    ? 'border-n-green/30 bg-n-green-bg text-n-green'
                    : 'border-n-border bg-n-card text-n-text-md hover:bg-n-hover hover:text-n-text'
                }`}
                >
                <Tag className="h-3.5 w-3.5" />
                {t('media_needs_alt_filter', 'Needs alt text')}
                {visibleNeedsAltCount > 0 && <span className="text-n-text-lt">{visibleNeedsAltCount}</span>}
              </button>

              <div className="flex rounded-n-sm border border-n-border bg-n-page p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                  className={`rounded-[9px] p-1.5 transition ${viewMode === 'grid' ? 'bg-n-card shadow-sm' : ''}`}
              >
                <Grid className="h-3.5 w-3.5 text-n-text-md" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                  className={`rounded-[9px] p-1.5 transition ${viewMode === 'list' ? 'bg-n-card shadow-sm' : ''}`}
              >
                <List className="h-3.5 w-3.5 text-n-text-md" />
              </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {workspacePanel && (
            <div className="mb-4 rounded-n border border-n-border bg-n-card shadow-n-sm">
              <div className="flex items-center justify-between border-b border-n-border px-4 py-3">
                <div>
                  <h2 className="text-sm font-semibold text-n-text">
                    {workspacePanel === 'cloud'
                      ? t('media_workspace_cloud_title', 'Cloud imports')
                      : workspacePanel === 'imports'
                        ? t('media_workspace_imports_title', 'Direct imports')
                        : workspacePanel === 'listening'
                          ? t('media_workspace_listening_title', 'Import from listening')
                          : t('media_workspace_submissions_title', 'Creator submissions')}
                  </h2>
                  <p className="mt-1 text-xs text-n-text-md">
                    {workspacePanel === 'cloud'
                      ? t(
                          'media_workspace_cloud_desc',
                          'Bring approved images and videos in from Drive or Dropbox without leaving the library.',
                        )
                      : workspacePanel === 'imports'
                        ? t(
                            'media_workspace_imports_desc',
                            'Import from a single URL, scan an RSS feed, or leave a provider hook ready for future Canva support.',
                          )
                        : workspacePanel === 'listening'
                          ? t(
                              'media_workspace_listening_desc',
                              'Save media from tracked mentions straight into the workspace for reuse.',
                            )
                          : t(
                              'media_workspace_submissions_desc',
                              'Share a lightweight upload link with creators, then review and approve inbound assets here.',
                            )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setWorkspacePanel(null)}
                  className="rounded-n-sm p-2 text-n-text-lt transition hover:bg-n-hover hover:text-n-text"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {workspacePanel === 'cloud' && (
                <div className="grid gap-4 px-4 py-4 lg:grid-cols-[240px_minmax(0,1fr)]">
                  <div className="space-y-3">
                    <div className="rounded-n-sm border border-n-border bg-n-page p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                        {t('media_cloud_connect_label', 'Connect provider')}
                      </p>
                      <div className="mt-3 flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => connectCloudProvider('google_drive')}
                          disabled={isConnectingCloud}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-n-sm border border-n-border bg-n-card px-3 text-sm font-medium text-n-text transition hover:bg-n-hover disabled:opacity-60"
                        >
                          {isConnectingCloud ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Cloud className="h-4 w-4 text-n-green" />
                          )}
                          {t('media_connect_google_drive', 'Connect Google Drive')}
                        </button>
                        <button
                          type="button"
                          onClick={() => connectCloudProvider('dropbox')}
                          disabled={isConnectingCloud}
                          className="inline-flex h-9 items-center justify-center gap-2 rounded-n-sm border border-n-border bg-n-card px-3 text-sm font-medium text-n-text transition hover:bg-n-hover disabled:opacity-60"
                        >
                          {isConnectingCloud ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Cloud className="h-4 w-4 text-n-green" />
                          )}
                          {t('media_connect_dropbox', 'Connect Dropbox')}
                        </button>
                      </div>
                    </div>

                    <div className="rounded-n-sm border border-n-border bg-n-page p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                        {t('media_cloud_accounts', 'Connected accounts')}
                      </p>
                      <div className="mt-3 space-y-2">
                        {cloudAccounts.length === 0 ? (
                          <p className="text-xs text-n-text-md">
                            {t('media_cloud_accounts_empty', 'No cloud accounts connected yet.')}
                          </p>
                        ) : (
                          cloudAccounts.map((account) => (
                            <button
                              key={account.id}
                              type="button"
                              onClick={() => browseCloudFiles(account.id)}
                              className={`flex w-full items-start justify-between rounded-n-sm border px-3 py-2 text-left transition ${
                                activeCloudAccountId === account.id
                                  ? 'border-n-green/30 bg-n-green-bg'
                                  : 'border-n-border bg-n-card hover:bg-n-hover'
                              }`}
                            >
                              <div>
                                <p className="text-sm font-medium text-n-text">{account.display_name}</p>
                                <p className="mt-0.5 text-xs text-n-text-md">
                                  {account.provider === 'google_drive' ? 'Google Drive' : 'Dropbox'}
                                  {account.email ? ` · ${account.email}` : ''}
                                </p>
                              </div>
                              <ExternalLink className="mt-0.5 h-3.5 w-3.5 text-n-text-lt" />
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-n-sm border border-n-border bg-n-page p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-n-text-lt" />
                        <input
                          value={cloudSearchQuery}
                          onChange={(event) => setCloudSearchQuery(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && activeCloudAccountId) {
                              setCloudNextPage(null);
                              browseCloudFiles(activeCloudAccountId, cloudSearchQuery);
                            }
                          }}
                          placeholder={t('media_cloud_search_placeholder', 'Search cloud assets...')}
                          className="h-9 w-full rounded-n-sm border border-n-border bg-n-card pl-9 pr-3 text-sm text-n-text outline-none focus:border-n-green focus:ring-2 focus:ring-n-green/10"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            activeCloudAccountId &&
                            browseCloudFiles(activeCloudAccountId, cloudSearchQuery)
                          }
                          disabled={!activeCloudAccountId || cloudIsLoading}
                          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-n-sm border border-n-border bg-n-card px-3 text-xs font-semibold text-n-text transition hover:bg-n-hover disabled:opacity-60"
                        >
                          {cloudIsLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                          )}
                          {t('media_refresh', 'Refresh')}
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {cloudFiles.length === 0 ? (
                        <div className="rounded-n-sm border border-dashed border-n-border bg-n-card px-4 py-10 text-center text-sm text-n-text-md">
                          {activeCloudAccountId
                            ? t('media_cloud_empty_state', 'No assets loaded yet. Browse an account or search above.')
                            : t('media_cloud_pick_account', 'Choose a connected account to browse importable assets.')}
                        </div>
                      ) : (
                        cloudFiles.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center gap-3 rounded-n-sm border border-n-border bg-n-card px-3 py-2"
                          >
                            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-n-sm bg-n-page">
                              {file.thumbnail_url ? (
                                <img
                                  src={file.thumbnail_url}
                                  alt={file.name}
                                  className="h-full w-full object-cover"
                                />
                              ) : file.type === 'video' ? (
                                <Video className="h-5 w-5 text-n-text-lt" />
                              ) : (
                                <Image className="h-5 w-5 text-n-text-lt" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-n-text">{file.name}</p>
                              <p className="mt-0.5 text-xs text-n-text-md">
                                {file.type} {file.size ? `· ${formatBytes(file.size)}` : ''}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                activeCloudAccountId &&
                                cloudImportMutation.mutate({
                                  accountId: activeCloudAccountId,
                                  fileId: file.id,
                                })
                              }
                              disabled={!activeCloudAccountId || cloudImportMutation.isPending}
                              className="inline-flex h-8 items-center gap-1.5 rounded-n-sm bg-n-green px-3 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                            >
                              {cloudImportMutation.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Upload className="h-3.5 w-3.5" />
                              )}
                              {t('media_import_button', 'Import')}
                            </button>
                          </div>
                        ))
                      )}
                    </div>

                    {cloudNextPage && activeCloudAccountId && (
                      <button
                        type="button"
                        onClick={() => browseCloudFiles(activeCloudAccountId, cloudSearchQuery, cloudNextPage)}
                        className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-card px-3 text-xs font-semibold text-n-text transition hover:bg-n-hover"
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                        {t('media_load_more', 'Load more')}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {workspacePanel === 'imports' && (
                <div className="grid gap-4 px-4 py-4 lg:grid-cols-2">
                  <div className="rounded-n-sm border border-n-border bg-n-page p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                      {t('media_import_url_title', 'Import from URL')}
                    </p>
                    <div className="mt-3 space-y-2">
                      <input
                        value={urlImportValue}
                        onChange={(event) => setUrlImportValue(event.target.value)}
                        placeholder={t('media_import_url_placeholder', 'https://example.com/image.jpg')}
                        className="h-10 w-full rounded-n-sm border border-n-border bg-n-card px-3 text-sm text-n-text outline-none focus:border-n-green focus:ring-2 focus:ring-n-green/10"
                      />
                      <input
                        value={urlImportFilename}
                        onChange={(event) => setUrlImportFilename(event.target.value)}
                        placeholder={t('media_import_filename_placeholder', 'Optional filename')}
                        className="h-10 w-full rounded-n-sm border border-n-border bg-n-card px-3 text-sm text-n-text outline-none focus:border-n-green focus:ring-2 focus:ring-n-green/10"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          urlImportMutation.mutate({
                            url: urlImportValue.trim(),
                            filename: urlImportFilename.trim() || undefined,
                          })
                        }
                        disabled={!urlImportValue.trim() || urlImportMutation.isPending}
                        className="inline-flex h-9 items-center gap-2 rounded-n-sm bg-n-green px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                      >
                        {urlImportMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Link2 className="h-4 w-4" />
                        )}
                        {t('media_import_url_button', 'Import asset')}
                      </button>
                    </div>
                  </div>

                  <div className="rounded-n-sm border border-n-border bg-n-page p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                      {t('media_import_rss_title', 'Import from RSS')}
                    </p>
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-2">
                        <input
                          value={rssUrl}
                          onChange={(event) => setRssUrl(event.target.value)}
                          placeholder={t('media_import_rss_placeholder', 'https://example.com/feed.xml')}
                          className="h-10 flex-1 rounded-n-sm border border-n-border bg-n-card px-3 text-sm text-n-text outline-none focus:border-n-green focus:ring-2 focus:ring-n-green/10"
                        />
                        <button
                          type="button"
                          onClick={() => discoverRssMutation.mutate({ url: rssUrl.trim() })}
                          disabled={!rssUrl.trim() || discoverRssMutation.isPending}
                          className="inline-flex h-10 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-card px-3 text-xs font-semibold text-n-text transition hover:bg-n-hover disabled:opacity-60"
                        >
                          {discoverRssMutation.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Search className="h-3.5 w-3.5" />
                          )}
                          {t('media_scan_feed', 'Scan feed')}
                        </button>
                      </div>
                      {rssFeedMeta && (
                        <div className="rounded-n-sm border border-n-border bg-n-card px-3 py-2 text-xs text-n-text-md">
                          <p className="font-semibold text-n-text">
                            {String(rssFeedMeta.title || t('media_rss_feed', 'Feed'))}
                          </p>
                          {rssFeedMeta.description ? (
                            <p className="mt-1 line-clamp-2">{String(rssFeedMeta.description)}</p>
                          ) : null}
                        </div>
                      )}
                      {rssDiscovery.length > 0 && (
                        <>
                          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                            {rssDiscovery.map((item) => {
                              const checked = selectedRssImages.includes(item.image_url);
                              return (
                                <label
                                  key={`${item.image_url}-${item.item_url || ''}`}
                                  className={`flex cursor-pointer items-start gap-3 rounded-n-sm border px-3 py-2 transition ${
                                    checked
                                      ? 'border-n-green/30 bg-n-green-bg'
                                      : 'border-n-border bg-n-card hover:bg-n-hover'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleRssSelection(item.image_url)}
                                    className="mt-1 h-4 w-4 rounded border-n-border text-n-green focus:ring-n-green/20"
                                  />
                                  <img
                                    src={item.image_url}
                                    alt={item.title || 'RSS item'}
                                    className="h-12 w-12 rounded-n-sm object-cover"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-n-text">
                                      {item.title || t('media_rss_image', 'RSS image')}
                                    </p>
                                    <p className="mt-1 text-xs text-n-text-md">
                                      {item.author || t('media_rss_unknown_author', 'Unknown author')}
                                    </p>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              importRssMutation.mutate({
                                feed_url: rssUrl.trim(),
                                items: selectedRssItems,
                              })
                            }
                            disabled={selectedRssItems.length === 0 || importRssMutation.isPending}
                            className="inline-flex h-9 items-center gap-2 rounded-n-sm bg-n-green px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                          >
                            {importRssMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4" />
                            )}
                            {t('media_import_selected_feed_images', 'Import selected images')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="rounded-n-sm border border-dashed border-n-border bg-n-page p-3 lg:col-span-2">
                    <div className="flex items-start gap-3">
                      <Sparkles className="mt-0.5 h-4 w-4 text-n-green" />
                      <div>
                        <p className="text-sm font-semibold text-n-text">
                          {t('media_canva_hook_title', 'Native-first import path')}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-n-text-md">
                          {t(
                            'media_canva_hook_desc',
                            'The native editor stays primary for now. This import surface is the provider hook we can extend to Canva or Adobe later without redesigning the workspace.',
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {workspacePanel === 'listening' && (
                <div className="px-4 py-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={listeningPlatform}
                        onChange={(event) => setListeningPlatform(event.target.value)}
                        className="h-9 rounded-n-sm border border-n-border bg-n-card px-3 text-sm text-n-text outline-none focus:border-n-green"
                      >
                        <option value="all">{t('media_listening_all_platforms', 'All platforms')}</option>
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="twitter">Twitter/X</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="youtube">YouTube</option>
                      </select>
                      <select
                        value={listeningDays}
                        onChange={(event) => setListeningDays(Number(event.target.value))}
                        className="h-9 rounded-n-sm border border-n-border bg-n-card px-3 text-sm text-n-text outline-none focus:border-n-green"
                      >
                        {[7, 14, 30, 60].map((days) => (
                          <option key={days} value={days}>
                            {t('media_listening_days', 'Last {{count}} days', { count: days })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => importListeningMutation.mutate({ mention_ids: selectedMentionIds })}
                      disabled={selectedMentionIds.length === 0 || importListeningMutation.isPending}
                      className="inline-flex h-9 items-center gap-2 rounded-n-sm bg-n-green px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                    >
                      {importListeningMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Radio className="h-4 w-4" />
                      )}
                      {t('media_import_mentions', 'Import selected mentions')}
                    </button>
                  </div>

                  <div className="mt-3 space-y-2">
                    {isFetchingListening ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-5 w-5 animate-spin text-n-text-lt" />
                      </div>
                    ) : listeningMentions.length === 0 ? (
                      <div className="rounded-n-sm border border-dashed border-n-border bg-n-page px-4 py-10 text-center text-sm text-n-text-md">
                        {t('media_listening_empty', 'No listening mentions with media were found for this filter.')}
                      </div>
                    ) : (
                      listeningMentions
                        .filter((mention) => mention.media_url)
                        .map((mention) => {
                          const checked = selectedMentionIds.includes(mention.id);
                          return (
                            <label
                              key={mention.id}
                              className={`flex cursor-pointer items-start gap-3 rounded-n-sm border px-3 py-3 transition ${
                                checked
                                  ? 'border-n-green/30 bg-n-green-bg'
                                  : 'border-n-border bg-n-card hover:bg-n-hover'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleMentionSelection(mention.id)}
                                className="mt-1 h-4 w-4 rounded border-n-border text-n-green focus:ring-n-green/20"
                              />
                              {mention.media_url ? (
                                <img
                                  src={mention.media_url}
                                  alt={mention.author_name || mention.author_handle || 'Mention media'}
                                  className="h-14 w-14 rounded-n-sm object-cover"
                                />
                              ) : (
                                <div className="flex h-14 w-14 items-center justify-center rounded-n-sm bg-n-page">
                                  <Image className="h-5 w-5 text-n-text-lt" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-sm font-semibold text-n-text">
                                    {mention.author_name || mention.author_handle || t('media_unknown_author', 'Unknown author')}
                                  </p>
                                  <span className="rounded-full bg-n-hover px-2 py-0.5 text-[10px] font-medium uppercase text-n-text-md">
                                    {mention.platform}
                                  </span>
                                </div>
                                {mention.content ? (
                                  <p className="mt-1 line-clamp-2 text-xs leading-5 text-n-text-md">
                                    {mention.content}
                                  </p>
                                ) : null}
                              </div>
                            </label>
                          );
                        })
                    )}
                  </div>
                </div>
              )}

              {workspacePanel === 'submissions' && (
                <div className="grid gap-4 px-4 py-4 lg:grid-cols-[280px_minmax(0,1fr)]">
                  <div className="rounded-n-sm border border-n-border bg-n-page p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                      {t('media_submission_link_label', 'Public upload link')}
                    </p>
                    <p className="mt-2 text-xs leading-5 text-n-text-md">
                      {t(
                        'media_submission_link_help',
                        'Share one secure upload link with creators or teammates. They can send media without needing app access.',
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => createSubmissionLinkMutation.mutate()}
                      disabled={createSubmissionLinkMutation.isPending}
                      className="mt-3 inline-flex h-9 items-center gap-2 rounded-n-sm bg-n-green px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                    >
                      {createSubmissionLinkMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Inbox className="h-4 w-4" />
                      )}
                      {t('media_create_submission_link', 'Create upload link')}
                    </button>

                    {submissionLink && (
                      <div className="mt-3 rounded-n-sm border border-n-border bg-n-card p-3">
                        <p className="text-xs font-medium text-n-text">{submissionLink}</p>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(submissionLink);
                            toast.success(t('media_submission_link_copied', 'Submission link copied.'));
                          }}
                          className="mt-2 inline-flex h-8 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-page px-3 text-xs font-semibold text-n-text transition hover:bg-n-hover"
                        >
                          <Copy className="h-3.5 w-3.5" />
                          {t('media_copy_link', 'Copy link')}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="rounded-n-sm border border-n-border bg-n-page p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-n-text-lt">
                      {t('media_submission_queue', 'Review queue')}
                    </p>
                    <div className="mt-3 space-y-3">
                      {submissions.length === 0 ? (
                        <div className="rounded-n-sm border border-dashed border-n-border bg-n-card px-4 py-10 text-center text-sm text-n-text-md">
                          {t('media_submissions_empty', 'No creator submissions yet.')}
                        </div>
                      ) : (
                        submissions.map((submission) => (
                          <div
                            key={submission.id}
                            className="rounded-n-sm border border-n-border bg-n-card p-3"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-n-sm bg-n-page">
                                {submission.thumbnail_url ? (
                                  <img
                                    src={submission.thumbnail_url}
                                    alt={submission.filename}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <FileText className="h-5 w-5 text-n-text-lt" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="truncate text-sm font-semibold text-n-text">
                                    {submission.filename}
                                  </p>
                                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                    submission.status === 'approved'
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : submission.status === 'rejected'
                                        ? 'bg-rose-50 text-rose-700'
                                        : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {submission.status}
                                  </span>
                                </div>
                                <p className="mt-1 text-xs text-n-text-md">
                                  {[submission.submitter_name, submission.submitter_email]
                                    .filter(Boolean)
                                    .join(' · ') || t('media_submission_unknown_submitter', 'Unknown submitter')}
                                </p>
                                {submission.submitter_note ? (
                                  <p className="mt-2 text-xs leading-5 text-n-text-md">
                                    {submission.submitter_note}
                                  </p>
                                ) : null}
                                <textarea
                                  value={submissionReviewNotes[submission.id] || ''}
                                  onChange={(event) =>
                                    setSubmissionReviewNotes((prev) => ({
                                      ...prev,
                                      [submission.id]: event.target.value,
                                    }))
                                  }
                                  placeholder={t('media_review_notes_placeholder', 'Optional review notes')}
                                  className="mt-3 min-h-[72px] w-full rounded-n-sm border border-n-border bg-n-page px-3 py-2 text-sm text-n-text outline-none focus:border-n-green"
                                />
                                {submission.status === 'pending' ? (
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        approveSubmissionMutation.mutate({
                                          submissionId: submission.id,
                                          reviewNotes: submissionReviewNotes[submission.id],
                                        })
                                      }
                                      disabled={approveSubmissionMutation.isPending}
                                      className="inline-flex h-8 items-center gap-1.5 rounded-n-sm bg-n-green px-3 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                                    >
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                      {t('media_approve_submission', 'Approve')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        rejectSubmissionMutation.mutate({
                                          submissionId: submission.id,
                                          reviewNotes: submissionReviewNotes[submission.id],
                                        })
                                      }
                                      disabled={rejectSubmissionMutation.isPending}
                                      className="inline-flex h-8 items-center gap-1.5 rounded-n-sm border border-n-border bg-n-page px-3 text-xs font-semibold text-n-text transition hover:bg-n-hover disabled:opacity-60"
                                    >
                                      <AlertCircle className="h-3.5 w-3.5" />
                                      {t('media_reject_submission', 'Reject')}
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2383e2' }} />
            </div>
          ) : displayedItems.length === 0 ? (
            <div className="rounded-n border border-dashed border-n-border bg-n-card px-6 py-16 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-n-green-bg text-n-green">
                <Image className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-n-text mb-2">
                {debouncedSearch || needsAltOnly
                  ? t('media_no_results', 'No results found')
                  : t('media_library_empty', 'Library is empty')}
              </h3>
              <p className="text-n-text-md text-sm mb-6 max-w-sm mx-auto">
                {debouncedSearch
                  ? t('media_no_match', 'No files match "{{query}}". Try a different search.', {
                      query: debouncedSearch,
                    })
                  : needsAltOnly
                    ? t('media_no_alt_needed', 'Everything visible here already has alt text.')
                  : t('media_empty_state', 'Upload images and videos to build your media library. Drag & drop files here or click Upload.')}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {!debouncedSearch && !needsAltOnly && (
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex h-9 items-center gap-2 rounded-n bg-n-green px-4 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    <Upload className="w-4 h-4" /> {t('media_upload_files', 'Upload Files')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate('/ai-images')}
                  className="inline-flex h-9 items-center gap-2 rounded-n border border-n-border bg-n-card px-4 text-[13px] font-semibold text-n-text transition-colors hover:bg-n-hover"
                >
                  <Sparkles className="h-4 w-4 text-n-green" />
                  {t('media_generate_with_ai', 'Generate with AI')}
                </button>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {displayedItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedId(item.id === selectedId ? null : item.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedId(item.id === selectedId ? null : item.id); }}
                  className={`group relative overflow-hidden rounded-n border bg-n-card shadow-n-sm transition-all hover:-translate-y-0.5 hover:shadow-n cursor-pointer ${
                    selectedId === item.id
                      ? 'border-n-green ring-2 ring-n-green/10'
                      : 'border-n-border'
                  }`}
                  aria-label={`Select ${item.filename}`}
                >
                  {(() => {
                    const compatibilityBadges = getPlatformCompatibilityBadges(item);
                    const isSelectedForBulk = selectedIds.includes(item.id);
                    return (
                      <>
                  <div className="aspect-square bg-n-page relative">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleSelection(item.id);
                      }}
                      className={`absolute left-2 bottom-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border shadow-sm transition ${
                        isSelectedForBulk
                          ? 'border-n-green bg-n-green text-white'
                          : 'border-white/80 bg-white/90 text-n-text'
                      }`}
                    >
                      {isSelectedForBulk ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    </button>
                    {item.media_type === 'image' && (
                      <div className="absolute bottom-2 right-2 z-10 flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            quickEnrichMutation.mutate(item.id);
                          }}
                          className="inline-flex h-8 items-center gap-1 rounded-full border border-white/80 bg-white/90 px-2.5 text-[10px] font-semibold text-n-text shadow-sm transition hover:bg-white"
                        >
                          {quickEnrichMutation.isPending && quickEnrichMutation.variables === item.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Wand2 className="h-3 w-3 text-n-green" />
                          )}
                          {t('media_quick_enrich', 'Enrich')}
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            quickPlatformReadyMutation.mutate(item.id);
                          }}
                          className="inline-flex h-8 items-center gap-1 rounded-full border border-white/80 bg-white/90 px-2.5 text-[10px] font-semibold text-n-text shadow-sm transition hover:bg-white"
                        >
                          {quickPlatformReadyMutation.isPending && quickPlatformReadyMutation.variables === item.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Sparkles className="h-3 w-3 text-n-green" />
                          )}
                          {t('media_quick_platform_ready', 'Ready')}
                        </button>
                      </div>
                    )}
                    {item.media_type === 'image' ? (
                      <img
                        src={item.thumbnail_url || item.url}
                        alt={item.alt_text || item.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : item.media_type === 'video' ? (
                      <div className="w-full h-full relative">
                        <VideoThumbnail
                          src={item.url}
                          poster={item.thumbnail_url}
                          alt={item.filename}
                          className="w-full h-full object-cover"
                        />
                        {/* Play icon overlay */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-9 h-9 rounded-full bg-n-card/80 backdrop-blur-sm flex items-center justify-center shadow-md">
                            <Play className="w-4 h-4 text-n-text ml-0.5" fill="currentColor" />
                          </div>
                        </div>
                        {item.duration !== null && item.duration !== undefined && (
                          <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
                            {formatDuration(item.duration)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="w-8 h-8 text-n-text-lt" />
                      </div>
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />

                    {/* Type badge */}
                    <span className={`absolute left-2 top-2 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white ${
                      item.media_type === 'image' ? 'bg-n-green' : item.media_type === 'video' ? 'bg-n-blue' : 'bg-n-text-md'
                    }`}>
                      {item.media_type}
                    </span>

                    {item.favorite && (
                      <span className="absolute bottom-2 left-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-amber-500 shadow-sm">
                        <Star className="h-4 w-4" fill="currentColor" />
                      </span>
                    )}

                    {/* Alt text indicator */}
                    <span className={`absolute right-2 top-2 rounded-full px-2 py-1 text-[10px] font-bold ${
                      item.alt_text
                        ? 'bg-n-green-bg text-n-green'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {item.alt_text ? t('media_alt_badge', 'ALT') : t('media_missing_alt_badge', 'Needs alt')}
                      </span>
                  </div>

                  <div className="space-y-2 p-3">
                    <p className="truncate text-[13px] font-semibold text-n-text">{item.filename}</p>
                    <div className="flex items-center justify-between gap-2 text-[11px] text-n-text-md">
                      <span>{formatBytes(item.file_size)}</span>
                      <span>{item.width && item.height ? `${item.width}×${item.height}` : timeAgo(item.created_at)}</span>
                    </div>
                    <p className="truncate text-[11px] text-n-text-md">
                      {sourceProviderLabel(item) || item.source_type || t('media_source_unknown_short', 'manual')}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {item.enrichment_status === 'completed' ? (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                          {t('media_enriched_badge', 'Enriched')}
                        </span>
                      ) : null}
                      {item.caption_suggestions.length > 0 ? (
                        <span className="rounded-full bg-n-hover px-2 py-0.5 text-[10px] font-medium text-n-text-md">
                          {t('media_caption_ready_badge', '{{count}} captions', { count: item.caption_suggestions.length })}
                        </span>
                      ) : null}
                      {item.provenance_provider ? (
                        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">
                          {item.provenance_provider}
                        </span>
                      ) : null}
                    </div>
                    {compatibilityBadges.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {compatibilityBadges.map((badge) => (
                          <span
                            key={badge.id}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              badge.tone === 'good'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {badge.label}
                          </span>
                        ))}
                      </div>
                    )}
                    {item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {item.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="rounded-full bg-n-hover px-2 py-0.5 text-[10px] font-medium text-n-text-md">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="bg-n-card rounded-n-sm border border-n-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-n-hover border-b border-n-border">
                    <th className="text-left px-4 py-3 font-medium text-n-text-md w-12"></th>
                    <th className="text-left px-2 py-3 font-medium text-n-text-md w-12"></th>
                    <th className="text-left px-4 py-3 font-medium text-n-text-md">{t('media_name', 'Name')}</th>
                    <th className="text-left px-4 py-3 font-medium text-n-text-md">{t('media_type', 'Type')}</th>
                    <th className="text-left px-4 py-3 font-medium text-n-text-md">{t('media_size', 'Size')}</th>
                    <th className="text-left px-4 py-3 font-medium text-n-text-md">{t('media_dimensions', 'Dimensions')}</th>
                    <th className="text-left px-4 py-3 font-medium text-n-text-md">{t('media_uploaded', 'Uploaded')}</th>
                    <th className="text-right px-4 py-3 font-medium text-n-text-md w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {displayedItems.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => setSelectedId(item.id === selectedId ? null : item.id)}
                      className={`border-b border-gray-100 hover:bg-n-hover cursor-pointer transition ${
                        selectedId === item.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-4 py-2">
                        <div className="w-10 h-10 rounded bg-n-page overflow-hidden relative">
                          {item.media_type === 'image' ? (
                            <img src={item.thumbnail_url || item.url} alt="" className="w-full h-full object-cover" />
                          ) : item.media_type === 'video' ? (
                            <>
                              <VideoThumbnail src={item.url} poster={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Play className="w-3 h-3 text-white drop-shadow" fill="currentColor" />
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FileText className="w-4 h-4 text-n-text-lt" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleSelection(item.id);
                          }}
                          className={`flex h-7 w-7 items-center justify-center rounded-full border transition ${
                            selectedIds.includes(item.id)
                              ? 'border-n-green bg-n-green text-white'
                              : 'border-n-border bg-white text-n-text-md'
                          }`}
                        >
                          {selectedIds.includes(item.id) ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Plus className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        <p className="text-gray-800 truncate max-w-xs">{item.filename}</p>
                        <p className="mt-1 truncate text-[11px] text-n-text-md">
                          {sourceProviderLabel(item) || item.source_type || t('media_source_unknown_short', 'manual')}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {item.enrichment_status === 'completed' ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                              {t('media_enriched_badge', 'Enriched')}
                            </span>
                          ) : null}
                          {getPlatformCompatibilityBadges(item).slice(0, 3).map((badge) => (
                            <span
                              key={badge.id}
                              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                badge.tone === 'good'
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}
                            >
                              {badge.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <span className="capitalize text-n-text-md">{item.media_type}</span>
                      </td>
                      <td className="px-4 py-2 text-n-text-md">{formatBytes(item.file_size)}</td>
                      <td className="px-4 py-2 text-n-text-md">
                        {item.width && item.height ? `${item.width}×${item.height}` : '—'}
                      </td>
                      <td className="px-4 py-2 text-n-text-md">{timeAgo(item.created_at)}</td>
                      <td className="px-4 py-2 text-right">
                        {item.media_type === 'image' && (
                          <>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                quickEnrichMutation.mutate(item.id);
                              }}
                              className="mr-1 p-1.5 text-n-text-lt hover:text-n-green hover:bg-n-hover rounded transition"
                              title={t('media_quick_enrich', 'Enrich')}
                            >
                              {quickEnrichMutation.isPending && quickEnrichMutation.variables === item.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Wand2 className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                quickPlatformReadyMutation.mutate(item.id);
                              }}
                              className="mr-1 p-1.5 text-n-text-lt hover:text-n-green hover:bg-n-hover rounded transition"
                              title={t('media_quick_platform_ready', 'Ready')}
                            >
                              {quickPlatformReadyMutation.isPending && quickPlatformReadyMutation.variables === item.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4" />
                              )}
                            </button>
                          </>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(item.id); }}
                          className="p-1.5 text-n-text-lt hover:text-red-500 hover:bg-red-50 rounded transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-n-sm hover:bg-n-hover disabled:opacity-40 transition"
              >
                <ChevronLeft className="w-4 h-4" /> {t('media_previous', 'Previous')}
              </button>
              <span className="text-sm text-n-text-md">
                {t('media_pagination', 'Page {{page}} of {{totalPages}}', { page, totalPages })}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-n-sm hover:bg-n-hover disabled:opacity-40 transition"
              >
                {t('media_next', 'Next')} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selectedItem && (
        <DetailPanel
          item={selectedItem}
          onClose={() => setSelectedId(null)}
          onDelete={() => deleteMutation.mutate(selectedItem.id)}
          onArchive={() => archiveMutation.mutate(selectedItem.id)}
          onCreatePostFromMedia={() => navigate(`/compose?newMediaId=${encodeURIComponent(selectedItem.id)}`)}
          onOpenInStudio={() => navigate(`/studio?section=post&newMediaId=${encodeURIComponent(selectedItem.id)}`)}
          onUpdate={(data) => updateMutation.mutate({ id: selectedItem.id, data })}
          isDeleting={deleteMutation.isPending}
          isUpdating={updateMutation.isPending}
          isArchiving={archiveMutation.isPending}
          onTrimVideo={() => setShowVideoTrimmer(true)}
        />
      )}

      {/* Video Trimmer Modal */}
      {showVideoTrimmer && selectedItem && selectedItem.media_type === 'video' && selectedItem.duration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <VideoTrimmer
            mediaId={selectedItem.id}
            videoUrl={selectedItem.url}
            duration={selectedItem.duration}
            onTrimComplete={(_newMedia: unknown) => {
              // Refresh the media library
              queryClient.invalidateQueries({ queryKey: ['media-library'] });
              queryClient.invalidateQueries({ queryKey: ['media-stats'] });
              setShowVideoTrimmer(false);
              toast.success(t('videotrim_success', 'Video trimmed successfully'));
            }}
            onCancel={() => setShowVideoTrimmer(false)}
          />
        </div>
      )}
    </div>
  );
}
